import { useAction } from '@cotera/client/v0/actions/framework';
import React from 'react';

import { Button } from '../ui/button';
import { Text, Tooltip } from '@cotera/client/app/components/ui';

import { OpenBillingSettings } from '../../actions/tabs/open-billing-settings.action';
import {
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import z from 'zod/v3';
import { BalanceInfoSchema } from '@cotera/api';
import { Assert } from '@cotera/utilities';

export const CreditBalanceSection: React.FC = () => {
  const USD_DECIMAL_PRECISION_DIGITS = 2;

  const { data: balanceInfoData } = useBalanceInfo({
    refetchIntervalMs: 1_000,
  });

  const openBillingSettings = useAction(OpenBillingSettings);

  if (balanceInfoData === null) {
    return null;
  }

  const balanceInfo = balanceInfoData;

  switch (balanceInfo.kind) {
    case 'orb-paygo-business': {
      const { canAddCC, currentCreditBalance } = balanceInfo;

      return (
        <Tooltip
          side="bottom"
          tooltipContent={
            <div className="flex flex-col gap-2">
              <Text.Caption>
                {canAddCC
                  ? 'Add a credit card to unlock an additional $20 in free credit'
                  : 'Go to Billing Settings'}
              </Text.Caption>
            </div>
          }
        >
          <Button
            variant="ghost"
            className="rounded-none h-full border-l border-r border-border text-foreground/80 hover:text-foreground rounded-r-none"
            onClick={async () => {
              await openBillingSettings.execute({});
            }}
          >
            Credit Balance: $
            {currentCreditBalance.toFixed(USD_DECIMAL_PRECISION_DIGITS)}
          </Button>
        </Tooltip>
      );
    }

    case 'orb-enterprise': {
      const {
        balance: { creditsCredits, engineeringHours: _ },
      } = balanceInfo;
      return (
        <Tooltip
          side="bottom"
          tooltipContent={
            <div className="flex flex-col gap-2">
              <Text.Caption>Go to Billing Settings</Text.Caption>
            </div>
          }
        >
          <Button
            variant="ghost"
            className="rounded-none h-full border-l border-r border-border text-foreground/80 hover:text-foreground rounded-r-none"
            onClick={async () => {
              await openBillingSettings.execute({});
            }}
          >
            {creditsCredits.kind === 'positive' ? (
              <>Credit Balance: {formatCredits(creditsCredits.credits)}</>
            ) : creditsCredits.kind === 'negative' ? (
              <>
                Credit Balance (overage):{' '}
                <span className="text-red-800">
                  - {formatCredits(creditsCredits.creditsOverageAmount)} ($
                  {formatCredits(creditsCredits.dollarsToBeCharged)})
                </span>
              </>
            ) : (
              Assert.unreachable(creditsCredits)
            )}
          </Button>
        </Tooltip>
      );
    }

    default:
      return Assert.unreachable(balanceInfo);
  }
};

type BalanceInfo = z.infer<typeof BalanceInfoSchema>;

export const useBalanceInfo = (params?: {
  refetchIntervalMs?: number;
}): UseSuspenseQueryResult<BalanceInfo | null> => {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);

  const refetchInterval = params?.refetchIntervalMs;

  return useSuspenseQuery<BalanceInfo | null>({
    queryKey: queryKeys.settings.billing.credit_balance(orgId),
    queryFn: async () => {
      const res = await client.billingSettings.balanceInfo({});

      if (res.isErr()) {
        return null;
      }

      return res.value.balanceInfo ?? null;
    },
    refetchInterval,
  });
};

/**
 * Formats large and small numbers of credits for readability.
 *
 * Adds commas for large numbers.
 * Adds decimal precision for smaller numbers.
 * Caps decimal precision if used.
 *
 * Largeness is defined as having an absolute value over 1000.
 */
export const formatCredits = (credits: number): string => {
  const isLarge = Math.abs(credits) >= 1000;

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: isLarge ? 0 : 2,
    maximumFractionDigits: isLarge ? 0 : 2,
  }).format(credits);
};
